import React from "react";
import Tooltip from '@mui/material/Tooltip';
import styled from "@emotion/styled";
import { useTheme } from "@mui/styles";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSelector } from "react-redux";
import { selectTeamRefinanceQuarterBreakdown } from "../../../../../slices/teamMonthlyStatsSlice";

const TeamQuarterlyBreakdown = () => {
  const theme = useTheme();

  const teamRefinanceQuarterBreakdown = useSelector(selectTeamRefinanceQuarterBreakdown)

  const tooltipSx = {
    color: theme.palette.common.black,
    backgroundColor: theme.palette.common.white,
    border: `0.1rem solid ${theme.palette.common.grayBorder}`,
    boxShadow: '0 0.7rem 1.7rem rgba(15, 23, 42, 0.14)',
    borderRadius: '0.65rem',
    maxWidth: '17rem',
    padding: '0.75rem 0.85rem',
    fontSize: '0.78rem',
    fontWeight: 650,
    lineHeight: 1.4,
  }

  const currentEmpty = teamRefinanceQuarterBreakdown.currentRefinances === 0
  const previousEmpty = teamRefinanceQuarterBreakdown.currentProgress === 'N/A'

  return (
    <BreakdownCard>
      <BreakdownHeader>
        <CardTitle>Quarterly Breakdown</CardTitle>
        <Tooltip
          componentsProps={{tooltip: {sx: tooltipSx}}}
          PopperProps={{modifiers: [{
            name: "offset",
            options: {
              offset: [0, -4],
            },
          }]}}
          title={
            <>
              Top: This quarter's refinance stats.
              <br />
              Middle: Last quarter's refinance stats.
              <br />
              Bottom: How this quarter's profits compare to last quarter's.
            </>
          } 
          placement="top"
          arrow
        >
          <HelpButton aria-label="Quarterly breakdown context" type="button">
            <HelpOutlineIcon />
          </HelpButton>
        </Tooltip>
      </BreakdownHeader>
      <QuarterGrid>
        <QuarterPanel>
          <QuarterLabel>{teamRefinanceQuarterBreakdown.currentQuarter}</QuarterLabel>
          {currentEmpty ?
            <QuarterEmpty>
              <span>no refinances</span>
              <span>in current quarter</span>
            </QuarterEmpty>
          :
            <QuarterValue>
              <span>
                {teamRefinanceQuarterBreakdown.currentRefinances.toLocaleString("en-US")}
                <small>refinances</small>
              </span>
              <span>
                $ {teamRefinanceQuarterBreakdown.currentGross.toLocaleString("en-US")}
                <small>gross profit</small>
              </span>
            </QuarterValue>
          }
        </QuarterPanel>
        <QuarterPanel>
          <QuarterLabel>{teamRefinanceQuarterBreakdown.previousQuarter}</QuarterLabel>
          {previousEmpty ?
            <QuarterEmpty>
              <span>no refinances</span>
              <span>in previous quarter</span>
            </QuarterEmpty>
          :
            <QuarterValue className="previous">
              <span>
                {teamRefinanceQuarterBreakdown.previousRefinances.toLocaleString("en-US")}
                <small>refinances</small>
              </span>
              <span>
                $ {teamRefinanceQuarterBreakdown.previousGross.toLocaleString("en-US")}
                <small>gross profit</small>
              </span>
            </QuarterValue>
          }
        </QuarterPanel>
      </QuarterGrid>
    </BreakdownCard>
  )
}

const BreakdownCard = styled.article(({ theme }) => `
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 0.42rem;
  padding: 0.48rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.62rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.16rem 0.5rem rgba(15, 23, 42, 0.035);
`)

const BreakdownHeader = styled.header(({ theme }) => `
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  padding-bottom: 0.35rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorderLight};
`)

const CardTitle = styled.h3(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 0.78rem;
  font-weight: 850;
  line-height: 1.2;
`)

const HelpButton = styled.button(({ theme }) => `
  width: 1.25rem;
  height: 1.25rem;
  min-width: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 999rem;
  background: transparent;
  color: ${theme.palette.common.grayText};
  cursor: help;

  svg {
    width: 0.95rem;
    height: 0.95rem;
  }

  &:hover {
    color: ${theme.palette.primary.dark};
    background: ${theme.palette.primary.labelTransparent};
  }
`)

const QuarterGrid = styled.div`
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: 1fr 1fr;
  gap: 0.35rem;
`

const QuarterPanel = styled.div(({ theme }) => `
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: auto auto;
  gap: 0.42rem;
  align-items: center;
  padding: 0.38rem 0.52rem;
  border: 0.1rem solid ${theme.palette.common.grayBorderLight};
  border-radius: 0.52rem;
  background: ${theme.palette.common.cardBackground};
`)

const QuarterLabel = styled.div(({ theme }) => `
  min-width: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.76rem;
  font-weight: 850;
  line-height: 1.2;
`)

const QuarterValue = styled.div(({ theme }) => `
  min-width: max-content;
  display: grid;
  gap: 1.6rem;
  justify-items: end;
  color: ${theme.closure.refinance.primary};
  font-size: 1.08rem;
  font-weight: 900;
  line-height: 1.05;
  height: 100%;

  &.previous {
    color: ${theme.palette.common.black};
  }

  span {
    display: grid;
    justify-items: end;
    font-size: 1.6rem;
    height: fit-content;
    margin: auto 0.6rem auto 0;
  }

  small {
    color: ${theme.palette.common.grayText};
    font-size: 0.76rem;
    font-weight: 800;
  }
`)

const QuarterEmpty = styled.div(({ theme }) => `
  display: flex;
  flex-direction: column;
  justify-items: end;
  color: ${theme.palette.common.grayText};
  line-height: 1.2;
  margin: 0 auto;
  width: 100%;

  span {
    color: ${theme.palette.common.grayText};
    font-size: 0.86rem;
    font-weight: 800;
  }
`)

export default TeamQuarterlyBreakdown;
